/**
 * AshrafieCoinGlyph
 *
 * A minted antique-gold coin bearing the "A" monogram, inspired by the
 * historical Ashrafie gold coin the rewards brand is named for. Drawn as inline
 * SVG with the physical cues that read as "real coin": a reeded (milled) edge,
 * an engraved inner ring, an embossed (relief) monogram, and a specular gloss.
 *
 * Per-instance gradient ids avoid collisions when several coins render at once.
 * Pass `shimmer` for a calm ambient glint that auto-disables under
 * prefers-reduced-motion.
 *
 * @module components/ashrafie/AshrafieCoinGlyph
 */

import React, { useId } from 'react';
import { motion, useReducedMotion } from 'motion/react';

export interface AshrafieCoinGlyphProps {
  /** Rendered pixel size (square). */
  size?: number;
  className?: string;
  /** Accessible label. Omit to render decorative (aria-hidden). */
  title?: string;
  /** Calm ambient glint on the coin face. Disabled under reduced motion. */
  shimmer?: boolean;
}

// The "A" monogram, sized to sit inside the engraved field (radius 25.5).
const A_PATH =
  'M 32 18 L 21 46 L 26.6 46 L 29 39 L 35 39 L 37.4 46 L 43 46 Z M 30 35 L 34 35 L 32 27 Z';

export const AshrafieCoinGlyph: React.FC<AshrafieCoinGlyphProps> = ({
  size = 22,
  className,
  title,
  shimmer = false,
}) => {
  const id = useId();
  const face = `${id}-face`;
  const rim = `${id}-rim`;
  const mono = `${id}-mono`;
  const gloss = `${id}-gloss`;
  const faceClip = `${id}-clip`;

  const reduce = useReducedMotion();
  const animate = shimmer && !reduce;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <defs>
        <radialGradient id={face} cx="38%" cy="30%" r="85%">
          <stop offset="0%" stopColor="#F7E9B8" />
          <stop offset="38%" stopColor="#E3C667" />
          <stop offset="74%" stopColor="#C49A3C" />
          <stop offset="100%" stopColor="#8A6A28" />
        </radialGradient>
        <linearGradient id={rim} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F2DD93" />
          <stop offset="50%" stopColor="#B98E36" />
          <stop offset="100%" stopColor="#6E5320" />
        </linearGradient>
        <linearGradient id={mono} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFF8E1" />
          <stop offset="100%" stopColor="#E0BF63" />
        </linearGradient>
        <radialGradient id={gloss} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
        <clipPath id={faceClip}>
          <circle cx="32" cy="32" r="25.5" />
        </clipPath>
      </defs>

      {/* Rim with milled (reeded) edge */}
      <circle cx="32" cy="32" r="31" fill={`url(#${rim})`} />
      <circle
        cx="32"
        cy="32"
        r="29"
        fill="none"
        stroke="#6E5320"
        strokeOpacity="0.5"
        strokeWidth="3"
        strokeDasharray="1.1 1.9"
      />

      {/* Coin face + engraved inner rings */}
      <circle cx="32" cy="32" r="25.5" fill={`url(#${face})`} />
      <circle
        cx="32"
        cy="32"
        r="22.6"
        fill="none"
        stroke="#6E5320"
        strokeOpacity="0.32"
        strokeWidth="1"
      />
      <circle
        cx="32"
        cy="32"
        r="21.5"
        fill="none"
        stroke="#FFF4CF"
        strokeOpacity="0.4"
        strokeWidth="0.7"
      />

      <g clipPath={`url(#${faceClip})`}>
        {/* Embossed monogram: dark engrave beneath, lit face on top */}
        <path
          d={A_PATH}
          fill="#5A431A"
          fillOpacity="0.45"
          transform="translate(0 1.1)"
        />
        <path
          d={A_PATH}
          fill={`url(#${mono})`}
          stroke="#7A5C24"
          strokeOpacity="0.5"
          strokeWidth="0.5"
        />

        {/* Specular gloss -- calm ambient shimmer when enabled */}
        <motion.ellipse
          cx="25"
          cy="20"
          rx="15"
          ry="9"
          fill={`url(#${gloss})`}
          initial={false}
          animate={animate ? { opacity: [0.45, 0.72, 0.45] } : { opacity: 0.55 }}
          transition={
            animate
              ? { duration: 4.5, repeat: Infinity, ease: 'easeInOut' }
              : undefined
          }
        />
      </g>
    </svg>
  );
};

export default AshrafieCoinGlyph;
